import { Controller, useController } from "react-hook-form";
import { classNames as cx } from "primereact/utils";

import { LabelValue } from "../LabelValue/LabelValue";
import { Dropdown } from "primereact/dropdown";
import {
  JobFrequencyEnum,
  frequencyLabelsByValue,
} from "../../pages/Sales/Contracts/CreateJobAndSchedule/CreateJobAndSchedule.model";
import {
  FrequencyPreset,
  frequencyPresetOptions,
  fromFrequencyPreset,
  toFrequencyPreset,
} from "../../pages/Sales/Contracts/CreateJobAndSchedule/frequency-preset";

import { InputNumber } from "primereact/inputnumber";
import { ReactNode } from "react";
import { If } from "../If/If";
import { Calendar } from "primereact/calendar";
import { months } from "../../constants/constants";

import classes from "./RecurrenceForm.module.scss";

interface RecurrenceFormProps {
  errors: any;
  control: any;
  getFormErrorMessage: (name: string) => ReactNode;
  frequency: JobFrequencyEnum;
  daysOfWeek: number[];
  handleWeekDayClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  recurrence?: boolean;
  startTime?: Date | null;
}

const RecurrenceForm = ({
  errors,
  control,
  getFormErrorMessage,
  frequency,
  daysOfWeek,
  handleWeekDayClick,
  recurrence = true,
  startTime,
}: RecurrenceFormProps) => {
  /*
    Both fields are taken once, here, rather than through two Controllers: the
    Frequency dropdown reads AND writes the pair, because "Bi-weekly" is not a
    stored frequency but `WEEKLY` with an interval of 2. Registering `recurEvery`
    twice - once for the dropdown and once for the spinner below - would be two
    registrations of one field.
  */
  const { field: frequencyField } = useController({ control, name: "frequency" });
  const { field: recurEveryField } = useController({ control, name: "recurEvery" });

  return (
    <div className={classes.recurrenceContainer}>
      <LabelValue label="Frequency">
        <Dropdown
          name={frequencyField.name}
          ref={frequencyField.ref}
          onBlur={frequencyField.onBlur}
          appendTo="self"
          options={frequencyPresetOptions}
          /*
            Derived on every render, never stored. Spinning the interval to 3
            flips the label back to "Weekly" on its own, and a job loaded at an
            interval of 3 keeps it.
          */
          value={toFrequencyPreset(frequencyField.value, recurEveryField.value)}
          onChange={(e) => {
            const next = fromFrequencyPreset(
              e.value as FrequencyPreset,
              recurEveryField.value
            );

            frequencyField.onChange(next.frequency);
            recurEveryField.onChange(next.recurEvery);
          }}
          style={{ width: "240px" }}
          className={cx({ "p-invalid": errors.frequency })}
        />
        {getFormErrorMessage("frequency")}
      </LabelValue>
      <div className={classes.recurField}>
        <span>Recur every</span>
        <InputNumber
          className={classes.recurNumberInput}
          value={recurEveryField.value}
          onValueChange={(e) => recurEveryField.onChange(e.value)}
          showButtons
          min={1}
        />
        <span>{frequencyLabelsByValue[frequency]} on</span>
        {/*
          The interval had no error line at all, so a cleared control blocked the
          submit with nothing on screen to explain it. Harmless on the forms that
          share this component and do not validate the field: errors[name] is
          then undefined and nothing renders.
        */}
        {getFormErrorMessage("recurEvery")}
      </div>
      <If condition={frequency === JobFrequencyEnum.WEEKLY}>
        <div className={classes.daysOfWeekField}>
          <span>On</span>
          <div className={classes.daysOfWeekField}>
            <div
              data-day-index={0}
              className={cx(classes.weekDay, {
                [classes.selected]: daysOfWeek.includes(0),
              })}
              onClick={handleWeekDayClick}
            >
              S
            </div>
            <div
              data-day-index={1}
              className={cx(classes.weekDay, {
                [classes.selected]: daysOfWeek.includes(1),
              })}
              onClick={handleWeekDayClick}
            >
              M
            </div>
            <div
              data-day-index={2}
              className={cx(classes.weekDay, {
                [classes.selected]: daysOfWeek.includes(2),
              })}
              onClick={handleWeekDayClick}
            >
              T
            </div>
            <div
              data-day-index={3}
              className={cx(classes.weekDay, {
                [classes.selected]: daysOfWeek.includes(3),
              })}
              onClick={handleWeekDayClick}
            >
              W
            </div>
            <div
              data-day-index={4}
              className={cx(classes.weekDay, {
                [classes.selected]: daysOfWeek.includes(4),
              })}
              onClick={handleWeekDayClick}
            >
              T
            </div>
            <div
              data-day-index={5}
              className={cx(classes.weekDay, {
                [classes.selected]: daysOfWeek.includes(5),
              })}
              onClick={handleWeekDayClick}
            >
              F
            </div>
            <div
              data-day-index={6}
              className={cx(classes.weekDay, {
                [classes.selected]: daysOfWeek.includes(6),
              })}
              onClick={handleWeekDayClick}
            >
              S
            </div>
          </div>
        </div>
      </If>
      <If condition={frequency === JobFrequencyEnum.MONTHLY}>
        <div>
          <LabelValue label="Day">
            <Controller
              name="dayOfMonth"
              render={({ field }) => (
                <InputNumber
                  className={classes.recurNumberInput}
                  value={field.value}
                  onValueChange={(e) => field.onChange(e.value)}
                  showButtons
                  max={31}
                  min={1}
                />
              )}
              control={control}
            />
            {getFormErrorMessage("dayOfMonth")}
          </LabelValue>
        </div>
      </If>
      <If condition={frequency === JobFrequencyEnum.YEARLY}>
        <div className={classes.daysYearly}>
          <LabelValue label="Day">
            <Controller
              name="dayOfMonth"
              render={({ field }) => (
                <InputNumber
                  className={classes.recurNumberInput}
                  value={field.value}
                  onValueChange={(e) => field.onChange(e.value)}
                  showButtons
                  max={31}
                  min={1}
                />
              )}
              control={control}
            />
            {getFormErrorMessage("dayOfMonth")}
          </LabelValue>
          <LabelValue label="Month">
            <Controller
              name="monthOfYear"
              render={({ field }) => (
                <Dropdown
                  options={months}
                  value={field.value}
                  optionLabel="name"
                  appendTo="self"
                  onChange={(e) => field.onChange(e.value)}
                  placeholder="Select a month"
                />
              )}
              control={control}
            />
            {getFormErrorMessage("monthOfYear")}
          </LabelValue>
        </div>
      </If>
      <If condition={recurrence}>
        <LabelValue label="Ends By" isRequired>
          <Controller
            name="recurrenceEndDate"
            render={({ field }) => (
              <Calendar
                id={field.name}
                value={field.value ? new Date(field.value) : undefined}
                onChange={(e) => field.onChange(e.value)}
                placeholder="MM/DD/YY"
                dateFormat="mm/dd/yy"
                showIcon
                minDate={
                  startTime
                    ? new Date(startTime)
                    : (() => {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        return tomorrow;
                      })()
                }
                className={cx({ "p-invalid": errors.recurrenceEndDate })}
                style={{ width: "240px", marginLeft: 0 }}
              />
            )}
            control={control}
          />
          {getFormErrorMessage("recurrenceEndDate")}
        </LabelValue>
      </If>
    </div>
  );
};

export default RecurrenceForm;
